import { getFirebaseApp, getFunctionsRegion } from "./firebase";
import {
  designToCloudCollections,
  type SharedRoomDoc,
} from "./sharedRoomTypes";
import type { Design } from "./types";
import { captureException, track } from "./telemetry";

/**
 * Thin client wrappers around the multiplayer-share Cloud Functions.
 *
 * `firebase/functions` is dynamically imported so callers don't pay the
 * SDK cost (or fail to load) until the share UI is actually opened.
 */

async function getCallable<T = unknown, R = unknown>(name: string) {
  const app = getFirebaseApp();
  if (!app) {
    throw new Error("Sharing isn't configured for this environment.");
  }
  const { getFunctions, httpsCallable, connectFunctionsEmulator } =
    await import("firebase/functions");
  const fns = getFunctions(app, getFunctionsRegion());
  const useEmulators =
    (import.meta.env.VITE_FIREBASE_USE_EMULATORS as string | undefined) ===
    "true";
  if (useEmulators) {
    try {
      connectFunctionsEmulator(fns, "localhost", 5001);
    } catch {
      // Already connected — safe to ignore.
    }
  }
  return httpsCallable<T, R>(fns, name);
}

/** `details.reason` the share functions attach when a free owner hits a
 *  collaboration limit (see `collabLimitDecision` in
 *  functions/src/shareInvitePolicy.ts). */
export const COLLAB_LIMIT_REASON = "COLLAB_LIMIT";
export type CollabLimit = "rooms" | "members";

/**
 * Which collaboration limit an error represents, or null. Looks through
 * `cause` so it also recognises a callable error wrapped by
 * `StartTogetherError`.
 */
export function collabLimitFromError(err: unknown): CollabLimit | null {
  let e: unknown = err;
  for (let depth = 0; depth < 4 && e && typeof e === "object"; depth++) {
    const details = (e as { details?: unknown }).details;
    if (
      details &&
      typeof details === "object" &&
      (details as { reason?: unknown }).reason === COLLAB_LIMIT_REASON
    ) {
      return (details as { limit?: unknown }).limit === "members" ? "members" : "rooms";
    }
    e = (e as { cause?: unknown }).cause;
  }
  return null;
}

function trackCollabLimit(err: unknown): void {
  const limit = collabLimitFromError(err);
  if (limit) track("collab_limit_reached", { limit });
}

export async function callCreateSharedRoom(input: {
  roomId: string;
  name: string;
  design: Design;
}): Promise<{ roomId: string }> {
  const fn = await getCallable<
    {
      roomId: string;
      name: string;
      design: Design;
      cloud: ReturnType<typeof designToCloudCollections>;
    },
    { roomId: string }
  >("createSharedRoom");
  const cloud = designToCloudCollections(input.design);
  try {
    const res = await fn({
      roomId: input.roomId,
      name: input.name,
      design: input.design,
      cloud,
    });
    track("share_room_promoted", { room_id: input.roomId });
    return res.data;
  } catch (err) {
    trackCollabLimit(err);
    if (!collabLimitFromError(err)) {
      captureException(err, { context: "callCreateSharedRoom", roomId: input.roomId });
    }
    throw err;
  }
}

export type CreateShareInviteInput =
  | { kind: "email"; roomId: string; granteeEmail: string }
  | {
      kind: "link";
      roomId: string;
      /** 1–10; server default 5. */
      maxUses?: number;
      /** 1–30; server default 14. */
      expiresInDays?: number;
    };

export async function callCreateShareInvite(
  input: CreateShareInviteInput
): Promise<{ inviteId: string; kind: "email" | "link" }> {
  const fn = await getCallable<
    CreateShareInviteInput,
    { inviteId: string; kind?: "email" | "link" }
  >("createShareInvite");
  try {
    const res = await fn(input);
    const kind = res.data.kind ?? input.kind;
    track("share_invite_sent", { room_id: input.roomId, kind });
    if (kind === "link") track("invite_link_created", { room_id: input.roomId });
    return { inviteId: res.data.inviteId, kind };
  } catch (err) {
    captureException(err, { context: "callCreateShareInvite", roomId: input.roomId });
    throw err;
  }
}

export interface AcceptShareInviteResult {
  roomId: string;
  /** Present once the deployed function returns them (see functions/src/share.ts). */
  roomName?: string;
  ownerDisplayName?: string;
  kind?: "email" | "link";
}

export async function callAcceptShareInvite(input: {
  inviteId: string;
}): Promise<AcceptShareInviteResult> {
  const fn = await getCallable<typeof input, AcceptShareInviteResult>(
    "acceptShareInvite"
  );
  try {
    const res = await fn(input);
    track("share_invite_accepted", {
      room_id: res.data.roomId,
      kind: res.data.kind ?? "email",
    });
    return res.data;
  } catch (err) {
    trackCollabLimit(err);
    if (!collabLimitFromError(err)) {
      captureException(err, { context: "callAcceptShareInvite" });
    }
    throw err;
  }
}

export async function callRevokeShareInvite(input: {
  inviteId: string;
}): Promise<void> {
  const fn = await getCallable<typeof input, { ok: boolean }>(
    "revokeShareInvite"
  );
  await fn(input);
}

export async function callRemoveShareMember(input: {
  roomId: string;
  uid: string;
}): Promise<void> {
  const fn = await getCallable<typeof input, { ok: boolean }>(
    "removeShareMember"
  );
  await fn(input);
}

export async function callLeaveSharedRoom(input: {
  roomId: string;
}): Promise<void> {
  const fn = await getCallable<typeof input, { ok: boolean }>(
    "leaveSharedRoom"
  );
  await fn(input);
}

export async function callDeleteSharedRoom(input: {
  roomId: string;
}): Promise<void> {
  const fn = await getCallable<typeof input, { ok: boolean }>(
    "deleteSharedRoom"
  );
  await fn(input);
}

/** Query param carrying the inviter's first name — display only, never
 *  trusted by the server. Lets `AcceptInvite` greet the recipient with
 *  "Sam invited you…" before they sign in. */
export const INVITE_FROM_PARAM = "from";

/** First name only, trimmed and capped, for the invite URL. */
export function inviterFirstName(displayName: string | null | undefined): string | null {
  if (!displayName) return null;
  const first = displayName.trim().split(/\s+/)[0] ?? "";
  return first.length > 0 ? first.slice(0, 40) : null;
}

/** Read the display-only inviter name from a URL search string. */
export function readInviteFromParam(
  search = typeof window === "undefined" ? "" : window.location.search
): string | null {
  const raw = new URLSearchParams(search).get(INVITE_FROM_PARAM);
  if (!raw) return null;
  const trimmed = raw.trim().slice(0, 40);
  return trimmed.length > 0 ? trimmed : null;
}

/** Build a shareable URL for an invite. */
export function buildInviteLink(
  inviteId: string,
  opts: { from?: string | null } = {}
): string {
  const from = inviterFirstName(opts.from);
  const search = new URLSearchParams();
  search.set("invite", inviteId);
  if (from) search.set(INVITE_FROM_PARAM, from);
  if (typeof window === "undefined") return `?${search.toString()}`;
  const url = new URL(window.location.href);
  url.search = `?${search.toString()}`;
  url.hash = "";
  return url.toString();
}

export type InviteShareMethod = "web_share" | "copy";

/**
 * Hand an invite link to the OS share sheet when the browser has one
 * (mobile Safari/Chrome), otherwise copy it to the clipboard. Returns the
 * method used, or `null` if the user dismissed the share sheet / both
 * paths failed. Emits `invite_link_shared` on success.
 */
export async function shareInviteLink(
  link: string,
  opts: { roomName?: string; from?: string | null } = {}
): Promise<InviteShareMethod | null> {
  const nav = typeof navigator === "undefined" ? null : navigator;
  const title = opts.roomName ? `Design "${opts.roomName}" with me` : "Design a room with me";
  const who = inviterFirstName(opts.from);
  const text = who
    ? `${who} invited you to design a room together in Room Canvas.`
    : "Let's design this room together in Room Canvas.";
  if (nav && typeof nav.share === "function") {
    try {
      const data: ShareData = { title, text, url: link };
      if (typeof nav.canShare !== "function" || nav.canShare(data)) {
        await nav.share(data);
        track("invite_link_shared", { method: "web_share" });
        return "web_share";
      }
    } catch (err) {
      // AbortError = user closed the sheet; fall through to the clipboard
      // only for non-abort failures so we don't surprise them.
      if (err instanceof DOMException && err.name === "AbortError") return null;
    }
  }
  try {
    await nav?.clipboard?.writeText(link);
    track("invite_link_shared", { method: "copy" });
    return "copy";
  } catch {
    return null;
  }
}

/** Build a deep-link URL straight to a shared room. */
export function buildSharedRoomLink(roomId: string): string {
  if (typeof window === "undefined") return `?shared=${roomId}`;
  const url = new URL(window.location.href);
  url.search = `?shared=${encodeURIComponent(roomId)}`;
  url.hash = "";
  return url.toString();
}

/** Re-export so callers don't have to import the type from two places. */
export type { SharedRoomDoc };
